import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import SocialCredentialsEditor from "./SocialCredentialsEditor";
import {
  Twitter, Facebook, Instagram, BarChart3, RefreshCw,
  CheckCircle2, XCircle, Clock, PauseCircle,
  Heart, Repeat2, MessageCircle, Share2, Eye, Smile,
} from "lucide-react";

const STATUS_PILL = {
  posted: "bg-[#E8F5E9] text-[#1B5E20] border-[#2E7D32]/40",
  skipped_no_credentials: "bg-[#F4EFE6] text-[#555] border-[#E5E0D8]",
  skipped_disabled: "bg-[#F4EFE6] text-[#555] border-[#E5E0D8]",
  failed: "bg-[#FFEBEE] text-[#C62828] border-[#D32F2F]/40",
};

// Human-friendly status labels + icons so admins don't have to decode `skipped_no_credentials`.
const STATUS_META = {
  posted: { label: "Live", Icon: CheckCircle2, color: "text-[#1B5E20]" },
  failed: { label: "Failed", Icon: XCircle, color: "text-[#C62828]" },
  skipped_no_credentials: { label: "Needs keys", Icon: PauseCircle, color: "text-[#A87900]" },
  skipped_disabled: { label: "Paused", Icon: PauseCircle, color: "text-[#555]" },
  pending: { label: "Queued", Icon: Clock, color: "text-[#1E3A8A]" },
  pending_retry: { label: "Retrying", Icon: Clock, color: "text-[#A87900]" },
};

// Engagement metrics → icon + readable label so the row reads
// "❤ 42 likes · 🔁 7 reshares" instead of "likes 42 retweets 7".
const ENGAGEMENT_META = {
  likes: { label: "Likes", Icon: Heart },
  retweets: { label: "Reshares", Icon: Repeat2 },
  reactions: { label: "Reactions", Icon: Smile },
  shares: { label: "Shares", Icon: Share2 },
  comments: { label: "Comments", Icon: MessageCircle },
  replies: { label: "Replies", Icon: MessageCircle },
  impressions: { label: "Impressions", Icon: Eye },
};

const TARGET_LABEL = { artwork: "Artwork", article: "Story", post: "Story", event: "Event", auction: "Auction" };

const PLATFORMS = [
  { id: "twitter", flag: "twitter_enabled", label: "X / Twitter", Icon: Twitter, env: ["TWITTER_API_KEY", "TWITTER_API_SECRET", "TWITTER_ACCESS_TOKEN", "TWITTER_ACCESS_TOKEN_SECRET"] },
  { id: "facebook", flag: "facebook_enabled", label: "Facebook Page", Icon: Facebook, env: ["FACEBOOK_PAGE_ID", "FACEBOOK_PAGE_ACCESS_TOKEN"] },
  { id: "instagram", flag: "instagram_enabled", label: "Instagram", Icon: Instagram, env: ["INSTAGRAM_USER_ID", "INSTAGRAM_ACCESS_TOKEN (or reuse FACEBOOK_PAGE_ACCESS_TOKEN)"] },
];

export default function AdminSocial() {
  const [settings, setSettings] = useState({ enabled: {}, configured: {} });
  const [posts, setPosts] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [s, p, a] = await Promise.all([
        api.get("/admin/social/settings"),
        api.get("/admin/social/posts?limit=50"),
        api.get("/admin/analytics/social?days=30"),
      ]);
      setSettings(s.data);
      setPosts(p.data);
      setAnalytics(a.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not load social data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const toggle = async (flag, value) => {
    try {
      const { data } = await api.put("/admin/social/settings", { [flag]: value });
      setSettings((s) => ({ ...s, enabled: data.enabled }));
      toast.success(`${flag.replace("_enabled", "")} auto-post ${value ? "enabled" : "disabled"}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Toggle failed");
    }
  };

  const retry = async (id) => {
    try {
      await api.post(`/admin/social/posts/${id}/retry`);
      toast.success("Retried · check status");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Retry failed");
    }
  };

  return (
    <div data-testid="admin-social" className="p-8 lg:p-12">
      <p className="label-eyebrow text-[#1E3A8A]">— Distribution</p>
      <h1 className="font-serif-display text-4xl font-medium tracking-tight text-[#111] mt-2">
        Social marketing
      </h1>
      <p className="text-sm text-[#555] mt-2 max-w-2xl">
        Auto-post artworks, articles and events to AfricaArtMasters&apos; official accounts on
        publish. Track inbound social traffic via UTM-tagged links.
      </p>

      {/* "How to read this page" — admins land here without context all the time. */}
      <div className="mt-6 border-l-2 border-[#F4C430] bg-[#F4EFE6]/50 px-4 py-3 text-xs text-[#555] max-w-3xl space-y-1">
        <p><strong className="text-[#111]">Channels</strong> — toggle which platforms post when content goes live. Green ✓ means keys are set; amber ⚠ means a credential is missing.</p>
        <p><strong className="text-[#111]">Inbound (last 30 days)</strong> — visitors who arrived from social. The taller the bar, the bigger the source.</p>
        <p><strong className="text-[#111]">Recent auto-posts</strong> — the actual queue. {`"Live"`} = sent; {`"Failed"`} = retry available; {`"Needs keys" / "Paused"`} = nothing happens until you fix the channel toggle above.</p>
      </div>

      {/* Platform toggles */}
      <section className="mt-10">
        <p className="label-eyebrow text-[#1E3A8A]">— Channels</p>
        <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
          {PLATFORMS.map(({ id, flag, label, Icon }) => {
            const configured = settings.configured?.[id];
            const enabled = settings.enabled?.[flag] ?? true;
            return (
              <div key={id} data-testid={`social-channel-${id}`} className="border border-[#E5E0D8] bg-white p-5">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Icon className="w-5 h-5 text-[#1E3A8A]" strokeWidth={1.5} />
                    <h3 className="font-serif-display text-xl text-[#111]">{label}</h3>
                  </div>
                  <Switch
                    data-testid={`toggle-${id}`}
                    checked={enabled}
                    disabled={!configured}
                    onCheckedChange={(v) => toggle(flag, v)}
                  />
                </div>
                <p className={`label-eyebrow mt-3 ${configured ? "text-[#1B5E20]" : "text-[#A87900]"}`}>
                  {configured ? "✓ Credentials configured" : "⚠ Credentials missing"}
                </p>
                {!configured && (
                  <p className="mt-3 text-xs text-[#555]">
                    Add credentials below to enable this channel.
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {/* Credentials editor (v1.27) — paste tokens directly into the DB */}
      <SocialCredentialsEditor credentials={settings.credentials || {}} onSaved={load} />

      {/* Inbound analytics */}
      <section className="mt-12">
        <p className="label-eyebrow text-[#1E3A8A] flex items-center gap-2">
          <BarChart3 className="w-3.5 h-3.5" />— Inbound (last 30 days)
        </p>
        {loading || !analytics ? (
          <p className="text-sm text-[#555] mt-4">— Loading</p>
        ) : (
          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="analytics-summary">
            <div className="border border-[#E5E0D8] bg-white p-5">
              <p className="label-eyebrow text-[#555]">Total visits</p>
              <p className="font-serif-display text-4xl mt-2 text-[#111]">{analytics.total_visits}</p>
            </div>
            <div className="border border-[#E5E0D8] bg-white p-5">
              <p className="label-eyebrow text-[#555]">Unique visitors</p>
              <p className="font-serif-display text-4xl mt-2 text-[#111]">{analytics.unique_visitors}</p>
            </div>
            <div className="border border-[#E5E0D8] bg-white p-5">
              <p className="label-eyebrow text-[#555]">Top source</p>
              <p className="font-serif-display text-2xl mt-2 text-[#1E3A8A] truncate">
                {analytics.by_source?.[0]?.label || "—"}
              </p>
              <p className="text-xs text-[#555] mt-1">
                {analytics.by_source?.[0]?.visits || 0} visits
              </p>
            </div>
          </div>
        )}

        {analytics && analytics.by_source?.length > 0 && (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div data-testid="by-source-table">
              <p className="label-eyebrow text-[#1E3A8A]">— By source</p>
              <ul className="mt-3 space-y-2">
                {analytics.by_source.map((s) => {
                  const max = analytics.by_source[0]?.visits || 1;
                  const pct = Math.round((s.visits / max) * 100);
                  return (
                    <li key={s.label} className="text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[#111]">{s.label}</span>
                        <span className="font-mono-aam text-xs text-[#555]">
                          {s.visits.toLocaleString()} · {s.unique_visitors.toLocaleString()} uniques
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#F4EFE6]">
                        <div
                          className="h-full bg-[#1E3A8A]"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div data-testid="top-paths-table">
              <p className="label-eyebrow text-[#1E3A8A]">— Top landing pages</p>
              <ul className="mt-3 space-y-2">
                {analytics.top_paths.map((p) => {
                  const max = analytics.top_paths[0]?.visits || 1;
                  const pct = Math.round((p.visits / max) * 100);
                  return (
                    <li key={p.path} className="text-sm">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[#111] truncate max-w-[260px]" title={p.path}>
                          {p.path || "—"}
                        </span>
                        <span className="font-mono-aam text-xs text-[#555]">
                          {p.visits.toLocaleString()}
                        </span>
                      </div>
                      <div className="h-1.5 bg-[#F4EFE6]">
                        <div className="h-full bg-[#F4C430]" style={{ width: `${pct}%` }} />
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>
        )}
      </section>

      {/* Recent posts log */}
      <section className="mt-12" data-testid="social-posts-log">
        <p className="label-eyebrow text-[#1E3A8A]">— Recent auto-posts</p>
        <p className="text-xs text-[#555] mt-1">
          Status pills show what happened. {`"Live"`} = sent successfully. {`"Needs keys"`} = a platform is still missing credentials. Click {`"Refresh"`} to pull engagement from the platform on demand.
        </p>
        <div className="mt-4 space-y-2">
          {posts.length === 0 ? (
            <p className="text-sm text-[#555]">No posts yet. Publish an artwork or article to test.</p>
          ) : (
            posts.map((p) => {
              const meta = STATUS_META[p.status] || STATUS_META.failed;
              const StatusIcon = meta.Icon;
              const targetLabel = TARGET_LABEL[p.target_kind] || p.target_kind;
              return (
                <div
                  key={p.id}
                  data-testid={`social-post-${p.id}`}
                  className="border border-[#E5E0D8] bg-white p-4 text-sm"
                >
                  <div className="flex items-start gap-4">
                    <StatusIcon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${meta.color}`} strokeWidth={1.75} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="label-eyebrow text-[#1E3A8A]">{p.platform}</span>
                        <span className="text-[#E5E0D8]">·</span>
                        <span className="label-eyebrow text-[#555]">{targetLabel}</span>
                        <span className={`label-eyebrow px-2 py-0.5 border ${STATUS_PILL[p.status] || STATUS_PILL.failed}`}>
                          {meta.label}
                        </span>
                      </div>
                      <p className="mt-2 text-[#111] line-clamp-2">{p.caption || "No caption"}</p>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      {p.post_url && (
                        <a
                          href={p.post_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="label-eyebrow text-[#1B5E20] hover:text-[#111]"
                        >
                          View post ↗
                        </a>
                      )}
                      {p.status === "failed" && (
                        <button
                          onClick={() => retry(p.id)}
                          data-testid={`retry-post-${p.id}`}
                          className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1"
                        >
                          <RefreshCw className="w-3 h-3" /> Retry
                        </button>
                      )}
                    </div>
                  </div>
                  {/* Engagement (v1.25) — friendly icon row + named refresh action. */}
                  {p.status === "posted" && (
                    <div className="mt-3 flex items-center gap-4 text-xs border-t border-[#E5E0D8]/60 pt-3 flex-wrap">
                      {p.engagement && Object.keys(p.engagement).length > 0 ? (
                        <>
                          {Object.entries(p.engagement).map(([k, v]) => {
                            const em = ENGAGEMENT_META[k] || { label: k, Icon: BarChart3 };
                            const Icon = em.Icon;
                            return (
                              <span
                                key={k}
                                data-testid={`engagement-${p.id}-${k}`}
                                className="inline-flex items-center gap-1.5 text-[#111]"
                                title={em.label}
                              >
                                <Icon className="w-3.5 h-3.5 text-[#1E3A8A]" />
                                <strong>{Number(v).toLocaleString()}</strong>
                                <span className="text-[#555] hidden sm:inline">{em.label}</span>
                              </span>
                            );
                          })}
                          <span className="ml-auto text-[#999]">
                            updated {new Date(p.engagement_fetched_at).toLocaleString()}
                          </span>
                        </>
                      ) : (
                        <span className="text-[#999]">
                          No engagement data yet — auto-fetches ~24h after publish
                        </span>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/admin/social/posts/${p.id}/fetch-engagement`);
                            toast.success("Engagement refreshed");
                            await load();
                          } catch (err) {
                            toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Fetch failed");
                          }
                        }}
                        data-testid={`fetch-engagement-${p.id}`}
                        className="label-eyebrow text-[#1E3A8A] hover:text-[#111] flex items-center gap-1"
                      >
                        <RefreshCw className="w-3 h-3" /> Refresh
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </section>
    </div>
  );
}
